import { PointerActivationConstraints } from '@dnd-kit/dom';
import { DragDropProvider, PointerSensor, type DragEndEvent } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { FC, useCallback, useMemo, useRef } from 'react';
import type { QualityZoneRanges } from '@/shared/lib';
import type { ChartAppearanceValues, PresetChartFormValues } from '../../model/dashboardPresetForm';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import { CHART_GRID_CLASS, chartGridRows, hasRoomForChart } from '../DashboardPresetGrid/chartLayout';
import type { CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';
import { AddChartTile } from './AddChartTile';
import { ChartPropertiesBlock } from './ChartPropertiesBlock';
import { ChartRowGap } from './ChartRowGap';
import { EditableChartCard } from './EditableChartCard';

type Props = {
  charts: PresetChartFormValues[];
  /** Фильтры панели страницы — диаграммы правящегося дашборда наполняются по ним же */
  filters: CustomChartPanelFilters;
  /** Пороги зон качества пользователя: диаграммы окрашены ими и в режиме правки */
  zoneRanges: QualityZoneRanges;
  /** Диаграмма, свойства которой развёрнуты под её строкой; `null` — все свойства свёрнуты */
  expandedChartId: string | null;
  /** Завести диаграмму за диаграммой с этой позицией (`-1` — первой на пустой сетке) */
  onAddChart: (afterIndex: number, startsRow: boolean) => void;
  onToggleProperties: (chartId: string) => void;
  /** Введённое в блоке описание остаётся за диаграммой и после сворачивания блока */
  onKeepDescription: (chartId: string, description: string) => void;
  onResolveChart: (chart: PresetChartFormValues) => void;
  onApplyProperties: (chart: PresetChartFormValues) => void;
  onRemoveChart: (chartId: string) => void;
  /** `startsRow` — диаграмма отпущена в промежуток между строками и открывает собой новую */
  onMoveChart: (fromIndex: number, toIndex: number, startsRow: boolean) => void;
  onResizeChart: (chartId: string, change: ChartSizeChange) => void;
  onRestyleChart: (chartId: string, appearance: ChartAppearanceValues) => void;
};

/** Порог в пикселях, после которого нажатие считается перетаскиванием, а не кликом по диаграмме. */
const DRAG_ACTIVATION_DISTANCE = 5;

/** Начало опознавателя промежутка между строками; за ним — место строки, под которой он стоит. */
const ROW_GAP_ID_PREFIX = 'row-gap-';

/** Место строки, под промежутком которой отпустили диаграмму; отпущена не в промежуток — `null`. */
const droppedRowGapIndex = (targetId: unknown): number | null => {
  if (typeof targetId !== 'string' || !targetId.startsWith(ROW_GAP_ID_PREFIX)) return null;
  const rowIndex = Number(targetId.slice(ROW_GAP_ID_PREFIX.length));
  return Number.isInteger(rowIndex) ? rowIndex : null;
};

/**
 * Сетка правящегося дашборда: та же раскладка, что у применённого пресета, но диаграммы
 * переставляются перетаскиванием, растягиваются границами и правятся своими свойствами.
 *
 * Место под новую диаграмму стоит в конце строки, где ещё осталась ширина под диаграмму, и
 * отдельной строкой в конце сетки — но только когда в последнюю строку диаграмма уже не
 * помещается: пока место в самой строке есть, отдельная строка была бы лишней.
 *
 * Блок свойств раскрывается не в ячейке своей диаграммы, а под всей её строкой во всю ширину
 * сетки: поля свойств не должны сжиматься до доли ширины показателя. Поэтому сетка складывается
 * строками, а не одним рядом ячеек, — блок встаёт за строкой, не уводя вниз ни соседок, ни место
 * под новую диаграмму.
 *
 * Перетаскивание задаёт не только место диаграммы в порядке, но и начало строки: под каждой
 * строкой стоит промежуток, и отпущенная в него диаграмма открывает собой новую строку, а
 * отпущенная в самой строке встаёт в неё наравне с соседками.
 */
export const DashboardEditGrid: FC<Props> = ({
  charts,
  filters,
  zoneRanges,
  expandedChartId,
  onAddChart,
  onToggleProperties,
  onKeepDescription,
  onResolveChart,
  onApplyProperties,
  onRemoveChart,
  onMoveChart,
  onResizeChart,
  onRestyleChart,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const sensors = useMemo(
    () => [
      PointerSensor.configure({
        activationConstraints: [new PointerActivationConstraints.Distance({ value: DRAG_ACTIVATION_DISTANCE })],
      }),
    ],
    [],
  );

  // Ширина сетки нужна на момент растягивания — измеряем по требованию, а не подпиской на размер.
  const getGridWidth = useCallback(() => gridRef.current?.getBoundingClientRect().width ?? 0, []);

  const rows = useMemo(() => chartGridRows(charts), [charts]);
  // Заполненная до конца последняя строка оставляет состав без места под новую диаграмму —
  // тогда его и даёт отдельная строка в конце сетки.
  const isTrailingRowShown = rows.length > 0 && !hasRoomForChart(rows[rows.length - 1].freeColumns);

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return;
    const { source, target } = event.operation;
    if (!isSortable(source)) return;

    // Отпущенная в промежуток диаграмма встаёт первой в новой строке — сразу за строкой, под
    // которой её отпустили. Место считается по составу дашборда: перестановка, показанная во
    // время перетаскивания, в него ещё не записана.
    const gapRowIndex = droppedRowGapIndex(target?.id);
    if (gapRowIndex !== null) {
      const lastIndex = rows[gapRowIndex]?.chartIndexes.at(-1);
      if (lastIndex === undefined) return;
      const { initialIndex } = source;
      onMoveChart(initialIndex, initialIndex <= lastIndex ? lastIndex : lastIndex + 1, true);
      return;
    }

    onMoveChart(source.initialIndex, source.index, false);
  };

  // Ячейки сетки собираются одним рядом, а не обёрткой на строку: диаграмма, переехавшая при
  // растягивании в соседнюю строку, иначе пересобирала бы развёрнутый под ней блок свойств — и
  // начатая в нём правка терялась бы.
  const cells = rows.flatMap((row, rowIndex) => {
    const rowCells = row.chartIndexes.map((index) => (
      <EditableChartCard
        key={charts[index].chartId}
        chart={charts[index]}
        index={index}
        filters={filters}
        zoneRanges={zoneRanges}
        isPropertiesExpanded={expandedChartId === charts[index].chartId}
        getGridWidth={getGridWidth}
        onToggleProperties={() => onToggleProperties(charts[index].chartId)}
        onRemove={() => onRemoveChart(charts[index].chartId)}
        onResize={(change) => onResizeChart(charts[index].chartId, change)}
      />
    ));

    if (hasRoomForChart(row.freeColumns)) {
      const lastIndex = row.chartIndexes[row.chartIndexes.length - 1];
      rowCells.push(
        <AddChartTile
          key={`add-after-${charts[lastIndex].chartId}`}
          startsRow={false}
          onAdd={() => onAddChart(lastIndex, false)}
        />,
      );
    }

    const expandedChart = row.chartIndexes
      .map((index) => charts[index])
      .find((chart) => chart.chartId === expandedChartId);
    if (expandedChart) {
      rowCells.push(
        <ChartPropertiesBlock
          key={`properties-${expandedChart.chartId}`}
          chart={expandedChart}
          filters={filters}
          // Ведущей может стать любая диаграмма дашборда, кроме самой правящейся: связь на себя
          // разбором не была бы.
          sourceCharts={charts
            .filter((item) => item.chartId !== expandedChart.chartId)
            .map((item) => ({ id: item.chartId, title: item.title }))}
          onResolve={onResolveChart}
          onKeepDescription={onKeepDescription}
          onResize={(change) => onResizeChart(expandedChart.chartId, change)}
          onRestyle={(appearance) => onRestyleChart(expandedChart.chartId, appearance)}
          onSave={onApplyProperties}
          onCancel={() => onToggleProperties(expandedChart.chartId)}
        />,
      );
    }

    rowCells.push(<ChartRowGap key={`${ROW_GAP_ID_PREFIX}${rowIndex}`} id={`${ROW_GAP_ID_PREFIX}${rowIndex}`} />);

    return rowCells;
  });

  return (
    <DragDropProvider sensors={sensors} onDragEnd={handleDragEnd}>
      <div ref={gridRef} className={CHART_GRID_CLASS}>
        {charts.length === 0 && <AddChartTile startsRow={false} onAdd={() => onAddChart(-1, false)} />}
        {cells}
        {isTrailingRowShown && <AddChartTile startsRow onAdd={() => onAddChart(charts.length - 1, true)} />}
      </div>
    </DragDropProvider>
  );
};
